<template>
  <div class="product-view">
    <!-- Editor goes here -->
    <div class="product-view__editor" :id="pitchprint.editor_id"></div>

    <el-row type="flex" :gutter="20">
      <!-- Preview images -->
      <el-col :xs="24" :sm="12">
        <Gallery :items="previewImages" />
      </el-col>

      <!-- Content -->
      <el-col :xs="24" :sm="12" class="flex flex-column">
        <div>
          <h2 class="product-view__headline">{{ item.title }}</h2>
          <div class="product-view__content">
            <div v-html="details.body" v-if="details.body"></div>

            <p class="m-t-3 text-center">
              <el-button type="primary" @click="onClickPersonalize" :disabled="!pitchprint.loaded" :loading="!pitchprint.loaded">
                {{ $t('Personalize now') }}
              </el-button>
            </p>
          </div>
        </div>

        <div v-if="details.body_template_description && pitchprint.data.projectId" class="m-t-3">
          <div v-html="details.body_template_description"></div>

          <p class="m-t-2 text-center" v-if="isPrintProduct">
            <a class="el-button el-button--default el-button--plain" :href="mailto">
              {{ $t('Order print product via eMail') }}
            </a>
          </p>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
/* eslint-disable no-undef */
import { mapGetters } from 'vuex';
import Gallery from '@/components/Misc/Gallery';
import * as ProjectService from '@/services/project';
import { find, get } from 'lodash';

export default {
  name: 'ProductPersonalize',

  components: {
    Gallery
  },

  props: {
    details: {
      type: Object,
      required: true,
      default() {
        return {
          body: '',
          body_template_description: '',
          title: '',
          template_type: {
            value: ''
          },
          order_email: ''
        };
      }
    },

    item: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapGetters({
      company: `user/company`,
      user: `user/currentUser`
    }),

    allDependanciesLoaded() {
      let result = false;

      const loadedDependancies = this.dependancies.filter(item => {
        return item.loaded;
      });

      if (loadedDependancies.length === this.dependancies.length) {
        result = true;
      }

      return result;
    },

    design() {
      if (!this.$route.params && !this.$route.params.id) {
        return null;
      }

      return this.$store.getters['templates/GET_DESIGN_BY_ID'](this.$route.params.id);
    },

    isEditMode() {
      let result = false;
      if (this.pitchprint.data && this.pitchprint.data.projectId) {
        result = true;
      }

      return result;
    },

    isPrintProduct() {
      let result = false;

      if (this.details) {
        result = this.details.template_type.value === 'print';
      }

      return result;
    },

    langCode() {
      const locale = get(this.user, 'locale', 'en_US');
      return locale.slice(0, 2);
    },

    mailto() {
      return `mailto:${this.details.order_email}?subject=${this.mailtoSubject}&body=${this.mailtoBody}`;
    },

    mailtoBody() {
      return `Bitte nennen Sie uns Ihre gewünschte Auflage und teilen Sie uns die Rechnungs- und Versandadresse mit. Hängen Sie bitte das Druckdokument dieser E-Mail an.`;
    },

    mailtoSubject() {
      return `Drucken von ${this.details.title}; ${this.company.name}`;
    },

    previewImages() {
      let result = this.item.previews || [];
      if (this.pitchprint.data && this.pitchprint.data.previews && this.pitchprint.data.previews.length) {
        result = this.pitchprint.data.previews;
      }

      return result;
    }
  },

  data() {
    return {
      loading: false,
      dependancies: [
        {
          name: 'jQuery',
          url: 'https://code.jquery.com/jquery-3.1.1.min.js',
          loaded: false
        },
        {
          name: 'PPCLIENT',
          url: 'https://pitchprint.io/rsc/js/client.js',
          loaded: false
        }
      ],
      pitchprint: {
        client: null,
        loaded: false,
        data: {},
        editor_id: 'pp_editor',
        iframe: null,
        logoItems: [],
        onEditorShownCount: 0
      }
    };
  },

  mounted() {
    this.init();
  },

  methods: {
    getLogoItemsInDesignTemplate(pages) {
      let items = [];

      pages.forEach((page, index) => {
        page.items.forEach(item => {
          if (['logo', 'logo_white', 'logo_black'].includes(item.title)) {
            item.pageNumber = index;
            items.push(item);
          }
        });
      });

      return items;
    },

    /**
     * Clean up PitchPrint to get a new instance started
     *
     */
    cleanUpPitchPrint() {
      const editor = document.getElementById(this.pitchprint.editor_id);

      if (editor.childNodes && editor.childNodes.length) {
        editor.childNodes.forEach(child => {
          if (child.tagName === 'IFRAME') {
            editor.removeChild(child);
          }
        });
      }
    },

    /**
     * Save project in own backend
     *
     * @param   {Object}    data
     */
    doSaveProject(data) {
      ProjectService.saveProject(data)
        .then(response => {
          this.handleProjectSaved(response);
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: this.$t('Error while saving project')
          });
        });
    },

    /**
     * Update project in own backend
     *
     * @param   {Object}    data
     */
    doUpdateProject(data) {
      ProjectService.updateProject(data.id, data)
        .then(response => {
          this.handleProjectSaved(response);
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: this.$t('Error while updating project')
          });
        });
    },

    /**
     * Handle project saved response made for own backend
     *
     * @param   {Object} response
     */
    handleProjectSaved(response) {
      if (response.data && response.data.id) {
        // Store project id from own backend
        this.pitchprint.data.own_project_id = response.data.id;
      }
    },

    async init() {
      // For own project id
      if (this.$route.query.id) {
        this.pitchprint.data.own_project_id = this.$route.query.id;
      }

      // For pitchprint project id
      if (this.$route.query.project_id) {
        this.pitchprint.data.projectId = this.$route.query.project_id;
        this.requestProject(this.$route.query.project_id);
      }

      // Get logo items in design template
      if (this.design && this.design.pages) {
        this.pitchprint.logoItems = this.getLogoItemsInDesignTemplate(this.design.pages);
      }

      this.loadDependancies();

      this.$store.dispatch('user/GET_CURRENT_USER');
    },

    /**
     * Initialize PitchPrint
     *
     */
    initPitchPrint() {
      let projectId = null;
      let mode = 'new';

      if (this.isEditMode) {
        projectId = this.pitchprint.data.projectId;
        mode = 'edit';
      }

      // Prepare user data to send to PitchPrint
      const userData = {
        user_id: get(this.user, 'id', ''),
        company_id: get(this.user, 'company.id', ''),
        project_id: get(this.pitchprint, 'data.projectId', ''),
        category_id: get(this.item, '_catId', ''),
        email: get(this.user, 'company.email', ''),
        name: `${this.user.first_name} ${this.user.last_name}`,
        company_name: get(this.user, 'company.name', ''),
        web: get(this.user, 'company.web', ''),
        slogan: get(this.user, 'company.slogan', ''),
        street_nr: get(this.user, 'company.street_nr', ''),
        zip: `${get(this.user, 'company.zip', '')}`,
        city: get(this.user, 'company.city', ''),
        phone: `${get(this.user, 'company.phone', '')}`,
        fax: `${get(this.user, 'company.fax', '')}`
      };

      this.pitchprint.client = new PitchPrintClient({
        apiKey: process.env.VUE_APP_PITCHPRINT_APIKEY,
        designId: this.item.designId,
        projectId: projectId,
        mode: mode,
        custom: true,
        langCode: this.langCode,
        userData
      });

      this.registerEvents();
    },

    /**
     * Load dependancy and inject script
     *
     * @param   {Object}    item
     */
    loadDependancy(item) {
      const scriptExist = document.getElementById(item.name);

      if (!scriptExist) {
        const script = document.createElement('script');
        script.src = item.url;
        script.id = item.name;

        document.body.appendChild(script);

        script.onload = () => {
          const currentDependancy = find(this.dependancies, obj => {
            return item.name === obj.name;
          });

          if (currentDependancy) {
            currentDependancy.loaded = true;
          }

          if (this.allDependanciesLoaded) {
            this.initPitchPrint();
          }
        };
      }
    },

    /**
     * Load all dependancies
     *
     */
    loadDependancies() {
      this.dependancies.forEach(item => {
        this.loadDependancy(item);
      });
    },

    /**
     * Handle event if PitchPrint has been fully loaded
     *
     */
    onAppValidated() {
      this.pitchprint.loaded = true;
      this.pitchprint.iframe = document.querySelector('iframe[src="https://pitchprint.io/index.html"]') || null;
    },

    onEditorShown() {
      if (this.pitchprint.onEditorShownCount > 0) {
        return false;
      }

      let showError = false;
      // const offsetLeft = 80; // Logo auf dem Platzhalter mittig positionieren

      if (Array.isArray(this.pitchprint.logoItems) && this.pitchprint.logoItems.length && !this.isEditMode) {
        // Hole das passende Firmenlogo und lade Logo zu PitchPrint hoch
        this.pitchprint.logoItems.forEach(item => {
          switch (item.title) {
            case 'logo':
              if (this.company.logo) {
                setTimeout(() => {
                  this.uploadImageToPitchPrint(this.company.logo, {
                    // x: item.left - offsetLeft,
                    // y: item.top,
                    pageNumber: item.pageNumber
                  });
                }, 2000);
              } else {
                showError = true;
              }
              break;
            case 'logo_black':
              if (this.company.logo_black) {
                setTimeout(() => {
                  this.uploadImageToPitchPrint(this.company.logo_black, {
                    // x: item.left - offsetLeft,
                    // y: item.top,
                    pageNumber: item.pageNumber
                  });
                }, 3000);
              } else {
                showError = true;
              }
              break;
            case 'logo_white':
              if (this.company.logo_white) {
                setTimeout(() => {
                  this.uploadImageToPitchPrint(this.company.logo_white, {
                    // x: item.left - offsetLeft,
                    // y: item.top,
                    pageNumber: item.pageNumber
                  });
                }, 4000);
              } else {
                showError = true;
              }
              break;
          }
        });

        if (showError) {
          this.$notify({
            type: 'error',
            title: this.$t('File not found'),
            message: this.$t('Please upload your company logo in different colors under company settings')
          });
        }
      }

      setTimeout(() => {
        this.pitchprint.client.fire('select-page', 0);

        if (this.pitchprint.iframe) {
          this.pitchprint.iframe.style.height = '800px';
          this.pitchprint.iframe.style.maxHeight = '800px';
        }

        this.pitchprint.client.fire('fit-page');
      }, 1000);

      this.pitchprint.onEditorShownCount++;
    },

    /**
     * Handle user click on customize button
     *
     */
    onClickPersonalize() {
      if (this.pitchprint.hasOwnProperty('client')) {
        this.pitchprint.client.showApp();
      } else {
        this.$notify({
          type: 'warning',
          title: this.$t('Editor not ready'),
          message: this.$t('Sorry, the editor to customize your design is not ready yet. Please wait a few seconds.')
        });
      }
    },

    /**
     * Handle user save a personalized design
     *
     * @param   {Object}    _val
     */
    onProjectSaved(_val) {
      let { data } = _val;

      if (this.isEditMode) {
        data.id = this.pitchprint.data.own_project_id;
        this.doUpdateProject(data);
      } else {
        this.doSaveProject(data);
      }

      this.pitchprint.data = data;

      this.$notify({
        title: this.$t('Success'),
        message: this.$t('Congratulation! Your design has been successfully saved.'),
        type: 'success'
      });

      this.reInitPitchPrint();
    },

    /**
     * Re-initialize PitchPrint
     *
     */
    reInitPitchPrint() {
      this.unregisterEvents();
      this.pitchprint.client = null;
      this.cleanUpPitchPrint();
      this.initPitchPrint();
    },

    /**
     * Register events
     *
     */
    registerEvents() {
      this.pitchprint.client.on('app-validated', this.onAppValidated);
      this.pitchprint.client.on('editor-shown', this.onEditorShown);
      this.pitchprint.client.on('project-saved', this.onProjectSaved);
    },

    /**
     * Remove dependancy
     *
     * @param   {Object}    item
     */
    removeDependancy(item) {
      const scriptExist = document.getElementById(item.name);

      if (scriptExist) {
        document.body.removeChild(scriptExist);
        delete window[item.name];
      }
    },

    /**
     * Remove dependancies
     *
     */
    removeDependancies() {
      this.dependancies.forEach(item => {
        this.removeDependancy(item);
      });
    },

    /**
     * Request PitchPrint project
     *
     * @param   {string}    id
     */
    requestProject(id) {
      return ProjectService.getProject(id)
        .then(response => {
          return response;
        })
        .catch(err => {
          return err;
        });
    },

    uploadImageToPitchPrint(file, params) {
      let { x, y, pageNumber } = params;
      const defaultOffset = 80;

      if (!x || x < defaultOffset) {
        x = defaultOffset;
      }

      if (!y || y < defaultOffset) {
        y = defaultOffset;
      }

      if (pageNumber !== undefined || pageNumber !== null) {
        this.pitchprint.client.fire('select-page', pageNumber);
      }

      const URL = process.env === 'production' ? file.data.full_url : 'https://directus.weltbewegend.com' + file.data.url;

      this.pitchprint.client.fire('load-image', {
        url: URL,
        type: 'shape',
        x,
        y,
        width: 1000,
        height: 1000
      });
    },

    /**
     * Unregister events
     *
     */
    unregisterEvents() {
      this.pitchprint.client.off('app-validated', this.onAppValidated);
      this.pitchprint.client.off('editor-shown', this.onEditorShown);
      this.pitchprint.client.off('project-saved', this.onProjectSaved);
    }
  },

  destroyed() {
    if (this.pitchprint.client) {
      this.unregisterEvents();
    }

    this.removeDependancies();
  }
};
</script>

<style lang="scss" scoped>
.product-view {
  &__headline {
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
